import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { getPublicSetting } from '../../utils/publicSettings';
import api from '../../api/axios';

interface ChatMessage {
  id: string;
  sender_type: 'customer' | 'admin';
  message: string;
  created_at: string;
}

interface SupportChat {
  id: string;
  status: string;
  last_customer_message_at: string | null;
  last_admin_reply_at: string | null;
}

const NO_REPLY_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

const ChatWidget: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [chat, setChat] = useState<SupportChat | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showWhatsappPrompt, setShowWhatsappPrompt] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState('https://wa.me/2349160353402');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    getPublicSetting('social_whatsapp', whatsappUrl).then(setWhatsappUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openChat = async () => {
    setIsOpen(true);
    if (!chat) {
      try {
        setIsLoading(true);
        const res = await api.get('/api/support/chat');
        if (res.data.success) setChat(res.data.data);
      } catch {
        // fail quietly — widget just won't load, doesn't block the site
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fetchMessages = useCallback(async () => {
    if (!chat) return;
    try {
      const res = await api.get(`/api/support/chat/${chat.id}/messages`);
      if (res.data.success) setMessages(res.data.data || []);
    } catch {}
  }, [chat]);

  useEffect(() => {
    if (chat && isOpen) {
      fetchMessages();
      pollRef.current = setInterval(fetchMessages, 5000);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
      };
    }
  }, [chat, isOpen, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // If the customer's last message has gone unanswered for 5+ minutes,
  // offer a direct WhatsApp fallback instead of leaving them waiting.
  useEffect(() => {
    if (!chat?.last_customer_message_at) {
      setShowWhatsappPrompt(false);
      return;
    }
    const check = () => {
      const lastCustomerMsg = new Date(chat.last_customer_message_at!).getTime();
      const lastAdminReply = chat.last_admin_reply_at ? new Date(chat.last_admin_reply_at).getTime() : 0;
      const answered = lastAdminReply > lastCustomerMsg;
      const waited = Date.now() - lastCustomerMsg;
      setShowWhatsappPrompt(!answered && waited > NO_REPLY_THRESHOLD_MS);
    };
    check();
    const interval = setInterval(check, 15000);
    return () => clearInterval(interval);
  }, [chat]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !chat) return;
    try {
      setIsSending(true);
      await api.post(`/api/support/chat/${chat.id}/messages`, { message: newMessage.trim() });
      setNewMessage('');
      await fetchMessages();
      setChat({ ...chat, last_customer_message_at: new Date().toISOString() });
    } catch {
    } finally {
      setIsSending(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <button
        onClick={() => (isOpen ? setIsOpen(false) : openChat())}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-primary text-white rounded-full shadow-lg flex items-center justify-center hover:bg-secondary transition-colors"
        aria-label="Chat with us"
      >
        {isOpen ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-6 z-40 w-[calc(100vw-3rem)] max-w-sm bg-white border border-light shadow-xl flex flex-col"
            style={{ height: '480px' }}
          >
            <div className="bg-primary text-white px-5 py-4">
              <p className="font-serif text-lg">Chat With Us</p>
              <p className="text-xs text-nude tracking-wide">We typically reply within a few minutes</p>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {isLoading ? (
                <p className="text-xs text-nude text-center mt-8">Loading...</p>
              ) : messages.length === 0 ? (
                <p className="text-xs text-nude text-center mt-8">
                  Send us a message and we'll get back to you shortly.
                </p>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] px-3 py-2 text-xs leading-relaxed ${
                        msg.sender_type === 'customer'
                          ? 'bg-primary text-white'
                          : 'bg-light text-primary'
                      }`}
                    >
                      {msg.message}
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {showWhatsappPrompt && (
              <div className="px-4 py-3 bg-amber-50 border-t border-amber-200">
                <p className="text-xs text-amber-800 leading-relaxed mb-2">
                  Still waiting on a reply? Chat with us directly on WhatsApp instead.
                </p>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block text-xs bg-primary text-white px-4 py-2 tracking-widest uppercase hover:bg-secondary transition-colors"
                >
                  Open WhatsApp
                </a>
              </div>
            )}

            <form onSubmit={handleSend} className="border-t border-light p-3 flex items-center space-x-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 bg-light px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-primary"
                disabled={!chat || isSending}
              />
              <button
                type="submit"
                disabled={!chat || isSending || !newMessage.trim()}
                className="bg-primary text-white p-2 hover:bg-secondary transition-colors disabled:opacity-50"
              >
                <Send size={14} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;